/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button, HStack, TabPanel, TabPanels, Text, VStack } from '@chakra-ui/react';
import { AiOutlineRead, AiOutlineSearch } from 'react-icons/ai';

import { SlEarphones, SlHeart } from 'react-icons/sl';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CategoryTag } from '@/pages/Bookstore/components/atoms/CategoryTag';
import { Input } from '@/pages/Bookstore/components/atoms/Input';
import { useTheme } from '@/pages/Bookstore/contexts/BookstoreThemeAdapter';
import { useBookstoreModule } from '@/pages/Bookstore/contexts/BookstoreModuleContext';
import { TopBookstoreMenu } from '@/pages/Bookstore/components/organisms/TopBookstoreMenu';
import { MainTemplate } from '@/templates/MainTemplate';
import { MyBooksTemplate } from './components/MyBookTemplate';

import {
  useBookCategories,
  useBooks,
  useGetFavoritedBooksMutation,
  useMothlyBestSellers,
  useMyReadingBooks,
} from '@/pages/Bookstore/services/hooks';
import { BookGrid } from '@/pages/Bookstore/components/organisms/BookGrid';
import { CurrentBook } from '@/pages/Bookstore/components/molecules/CurrentBook';
import { HorizontalBooks } from '@/pages/Bookstore/components/molecules/HorizontalBooks';
import { BookstoreBanners } from '@/pages/Bookstore/components/molecules/BookstoreBanners';
import { FaNewspaper } from 'react-icons/fa';
import { InfoBox } from '@/pages/Bookstore/components/atoms/InfoBox';
import { OnboardingBalloon, useBookstoreOnboarding } from '@/pages/Bookstore/components/organisms/BookstoreOnboarding';
import { RiBookLine, RiBookOpenLine } from 'react-icons/ri';
import { BookLoadingOverlay } from '@/pages/Bookstore/components/organisms/BookLoadingOverlay';
import { useTheme as useAppTheme } from '@/contexts/ThemeProvider';
import { useProfileSubscriptions } from '@/service/user/hooks';
import { isAuthenticated } from '@/service/axios';

function NoSubscriptionBox({ productId, color }: { productId?: number; color: string }) {
  const navigate = useNavigate();
  return (
    <Box
      w="100%"
      paddingX="5%"
      borderLeft="4px solid"
      borderLeftColor={color}
      bg="gray.50"
      borderRadius="8px"
      p="14px 16px"
    >
      <Text fontWeight="700" fontSize="14px" color="gray.700" mb="4px">
        Você ainda não possui uma assinatura
      </Text>
      <Text fontSize="13px" color="gray.600" mb="12px">
        Assine para receber obras que serão suas para sempre. Enquanto isso, aproveite o Acervo gratuito.
      </Text>
      <Button
        size="sm"
        bg={color}
        color="white"
        _hover={{ opacity: 0.9 }}
        onClick={() => navigate(productId ? `/perfil/planos?produto=${productId}` : '/perfil/planos')}
      >
        Conhecer o produto
      </Button>
    </Box>
  );
}

interface Category {
  id: number;
  name: string;
}

function useCategoryTags(
  categories: { results?: Category[] } | Category[] | undefined,
  tagSet: Set<number>,
  setTagSet: React.Dispatch<React.SetStateAction<Set<number>>>
) {
  // Normaliza: backend pode entregar { results: [...] } OU array direto.
  const list: Category[] | undefined = Array.isArray(categories) ? categories : categories?.results;
  return useMemo(
    () =>
      list
        ?.map((c) => ({ ...c, active: tagSet.has(c.id) ? 1 : 0 }))
        .sort((a, b) => b.active - a.active)
        .map((c) => (
          <CategoryTag
            key={c.id}
            active={tagSet.has(c.id)}
            onClick={() => {
              setTagSet((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(c.id)) {
                  newSet.delete(c.id);
                } else {
                  newSet.add(c.id);
                }
                return newSet;
              });
            }}
          >
            {c.name.toLocaleLowerCase()}
          </CategoryTag>
        )),
    [tagSet, list, setTagSet]
  );
}

// Convênios DTEL (2027), ATEL (768) e JR (2635): abas na ordem Acervo -> Lendo -> Minhas Obras -> Favoritos
const ACERVO_FIRST_AGREEMENTS = [2027, 768, 2635];

export function MyBooksPage() {
  const { bookstoreTypes, product, showBestSellers, colors } = useTheme();
  const { isProductSuperApp, getValidSubscriptions, productData, id: agreementId } = useAppTheme();
  const acervoFirst = ACERVO_FIRST_AGREEMENTS.includes(Number(agreementId));
  const { data: subscriptions = [], isFetched: subscriptionsFetched } = useProfileSubscriptions();
  const subscriptionsReady = subscriptionsFetched || !isAuthenticated();
  const hasSubscription = isProductSuperApp
    ? subscriptions.some((sub) => Number(sub.plan.product) === Number(product?.id))
    : getValidSubscriptions(subscriptions).length > 0;
  // conta avulsa: sem assinatura não há "Minhas Obras", então abre direto no Acervo
  const noSubscription = subscriptionsReady && isAuthenticated() && !hasSubscription;
  const [tagSet, setTagSet] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState<string>('');
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const goBack = (location.state as { from?: string } | null)?.from ? () => navigate(-1) : undefined;

  const moduleType = useBookstoreModule()?.moduleType;

  // Regra: revistas e livros/audiobooks são mutuamente exclusivos.
  // `moduleType` (vindo do BookstoreModuleProvider) tem prioridade quando definido;
  // caso contrário cai pro metadata do `product`, e por último pro `bookstoreTypes`.
  const isMagazine =
    moduleType === 'banca' ||
    (moduleType !== 'biblioteca' &&
      moduleType !== 'audiobook' &&
      (product ? product?.metadata?.hasmagazine?.value === 'true' : bookstoreTypes.includes('magazine')));

  const isAudiobook =
    !isMagazine && (product ? product?.metadata?.hasaudiobook?.value === 'true' : bookstoreTypes.includes('audiobook'));

  const isBook = !isMagazine && (product ? product.defaultProduct.isBookstore : bookstoreTypes.includes('book'));
  const categoryType = isMagazine ? 'magazine' : isBook ? 'book' : 'audiobook';
  const { data: categories } = useBookCategories(categoryType);
  const showBestSellersProduct = product ? product?.metadata?.isbestsellers?.value === 'true' : showBestSellers;
  const shouldMergeBooksAndAudiobooks = isBook && isAudiobook;

  useEffect(() => {
    const categoryParam = params.get('categorias');
    if (!categoryParam) return;
    setTimeout(() => {
      setTagSet(new Set(categoryParam.split(',').map((cid) => parseInt(cid))));
    }, 800);
  }, [params]);

  const tags = useCategoryTags(categories, tagSet, setTagSet);

  const meusTab =
    ((isBook || isAudiobook) && {
      name: 'Minhas Obras',
      icon: shouldMergeBooksAndAudiobooks ? <RiBookLine /> : isBook ? <RiBookLine /> : <SlEarphones />,
    }) ||
    (isMagazine && { name: 'Minhas Revistas', icon: <FaNewspaper /> });

  const tabs = [
    !acervoFirst && meusTab,
    { name: 'Acervo', icon: <RiBookOpenLine /> },
    { name: 'Lendo', icon: <AiOutlineRead /> },
    acervoFirst && meusTab,
    { name: 'Favoritos', icon: <SlHeart /> },
  ].filter(Boolean) as { name: string; icon: React.JSX.Element }[];

  const acervoTabIndex = tabs.findIndex((t) => t.name === 'Acervo');
  const meusTabIndex = tabs.findIndex((t) => t.name === 'Minhas Obras' || t.name === 'Minhas Revistas');
  const {
    step: onboardingStep,
    controlled,
    tabIndex,
    handleTabChange,
    confirmMeus,
    confirmAcervo,
  } = useBookstoreOnboarding(
    acervoTabIndex,
    isMagazine ? 'banca' : 'biblioteca',
    // tutorial só na biblioteca quando o tenant também tem livro
    subscriptionsReady && !(isMagazine && productData?.hasBookstore),
    noSubscription,
    meusTabIndex
  );

  const meusPanel =
    isBook || isAudiobook ? (
      <TabPanel padding="0px">
        <VStack mt="20px" width="100%" align="flex-start" spacing={5}>
          <BookstoreBanners />
          <CurrentBook />
          <InfoBox
            title="Minhas Obras"
            description="Estes e-books e audiobooks são seus para sempre. Com ISBN incluso, você pode baixá-los quando quiser realizando o download."
            highlight="Os usuários receberão as mídias até o dia 5."
            borderColor={shouldMergeBooksAndAudiobooks ? '#14B8A6' : isBook ? '#3B82F6' : '#F59E0B'}
            bg={shouldMergeBooksAndAudiobooks ? '#F0FDFA' : isBook ? '#EFF6FF' : '#FFFBEB'}
          />
          {onboardingStep === 'meus' && (
            <OnboardingBalloon
              text="Essas obras são suas para sempre! Você pode baixar quando quiser e ler em outras plataformas se assim desejar."
              onConfirm={confirmMeus}
            />
          )}
          {/*        <HorizontalBooks
                name={'Ebooks disponibilizados neste mês'}
                receivedBook
                params={{ type: 'book', product_id: product?.id }}
                hookApi={useFeaturedBooks}
                description={
                  'Estes títulos foram disponibilizados para você e podem ser baixados para uso permanente.'
                }
                borderColor="#3B82F6"
                bg="#EFF6FF"
              />
 */}
          {noSubscription && <NoSubscriptionBox productId={product?.id} color={colors.primaryColor} />}
          <MyBooksTemplate
            emptyLabel="obras"
            filter={{
              ...(shouldMergeBooksAndAudiobooks ? {} : { book__type: isBook ? 'book' : 'audiobook' }),
              product_id: product?.id,
            }}
          />
        </VStack>
      </TabPanel>
    ) : isMagazine ? (
      <TabPanel padding="0px">
        <VStack mt="20px" width="100%" align="flex-start" spacing={5}>
          <BookstoreBanners />
          <InfoBox
            title="Minhas Revistas"
            description="Estas revistas são suas para sempre. Com ISSN incluso, você pode baixá-las quando quiser realizando o download."
            highlight="Os usuários receberão as mídias até o dia 20."
            borderColor="#8B5CF6"
            bg="#F5F3FF"
          />
          {onboardingStep === 'meus' && (
            <OnboardingBalloon
              text="Estas revistas são suas para sempre! Você pode baixá-las quando quiser e ler em outras plataformas se assim desejar."
              onConfirm={confirmMeus}
            />
          )}

          {/* <HorizontalBooks
                name={'Revista do mês'}
                receivedBook
                params={{ type: 'magazine', product_id: product?.id }}
                hookApi={useFeaturedBooks}
                description={
                  'A edição do mês é carregada automaticamente e integra o histórico de revistas recebidas.'
                }
                borderColor="#8B5CF6"
                bg="#F5F3FF"
              /> */}

          {noSubscription && <NoSubscriptionBox productId={product?.id} color={colors.primaryColor} />}
          <MyBooksTemplate
            emptyLabel="revistas"
            filter={{
              book__type: 'magazine',
              product_id: product?.id,
            }}
          />
        </VStack>
      </TabPanel>
    ) : null;

  return (
    <MainTemplate noShadow goBack={goBack}>
      <BookLoadingOverlay />
      <TopBookstoreMenu
        key={noSubscription ? 'avulsa' : 'assinante'}
        tabs={tabs}
        selectedTab={noSubscription ? acervoTabIndex : 0}
        {...(controlled ? { tabIndex, onTabChange: handleTabChange } : {})}
      >
        <TabPanels>
          {!acervoFirst && meusPanel}

          {/* Catálogo */}
          <TabPanel padding="0px">
            <VStack mt="20px" width="100%" spacing={5} align="flex-start">
              <InfoBox
                title={isMagazine ? 'Revistas Gratuitas' : 'Ebooks Gratuitos'}
                description={
                  isMagazine
                    ? 'O Acervo abaixo é disponibilizado gratuitamente para leitura online e não integra a aquisição das revistas editoriais.'
                    : 'O Acervo abaixo é disponibilizada gratuitamente para leitura online e não integra a aquisição das obras editoriais.'
                }
                borderColor="#10B981"
                bg="#ECFDF5"
              />
              {onboardingStep === 'acervo' && (
                <OnboardingBalloon
                  text="Este é o seu acervo de títulos gratuitos para leitura diretamente na plataforma"
                  onConfirm={confirmAcervo}
                />
              )}
              {/*               
                {isMagazine && hasDigitalMagazines && <HorizontalMagazines agreementId={id ?? undefined} limit={5} />}
              */}
              {isBook && (
                <HorizontalBooks
                  hookApi={useMothlyBestSellers}
                  name="Livros do mês"
                  bestSellerName="Destaques"
                  showEditorialCode={false}
                  description="Confira as obras que estão em alta e conquistando leitores."
                  params={{ product_id: product?.id, categories: '162417' }}
                  borderColor="#D97706"
                  bg="#FEF3C7"
                />
              )}
              {/*            
              {showBestSellersProduct && isBook && product?.id && (
                <HorizontalBooks
                  hookApi={useMothlyBestSellers}
                  name="Best Sellers"
                  hideIfEmpty
                  description="As obras mais vendidas do momento."
                  params={{ product_id: product?.id, categories: '143842' }}
                  borderColor="#EF4444"
                  bg="#FEF2F2"
                />
              )} */}
              {showBestSellersProduct && isBook && product?.id && (
                <HorizontalBooks
                  hookApi={useMothlyBestSellers}
                  name="Lançamentos"
                  hideIfEmpty
                  showEditorialCode={false}
                  description="As novidades que acabaram de chegar."
                  params={{ product_id: product?.id, categories: '143843' }}
                  borderColor="#4ddaab"
                  bg="#ECFDF5"
                />
              )}
              {showBestSellersProduct && isBook && product?.id && (
                <HorizontalBooks
                  hookApi={useMothlyBestSellers}
                  name="Volta às Aulas"
                  hideIfEmpty
                  showEditorialCode={false}
                  description="Seleção especial para o início do ano letivo."
                  params={{ product_id: product?.id, categories: '143844' }}
                  borderColor="#06B6D4"
                  bg="#ECFEFF"
                />
              )}
              {showBestSellersProduct && isBook && product?.id && (
                <HorizontalBooks
                  hookApi={useMothlyBestSellers}
                  name="Negócios"
                  hideIfEmpty
                  showEditorialCode={false}
                  description="Os melhores títulos sobre negócios e empreendedorismo."
                  params={{ product_id: product?.id, categories: '143913' }}
                  borderColor="#6366F1"
                  bg="#EEF2FF"
                />
              )}
              {showBestSellersProduct && isBook && product?.id && (
                <HorizontalBooks
                  name="Explore os nossos Best Sellers"
                  params={{ type: 'book', random: 'true', is_best_seller: true, product_id: product?.id }}
                  hookApi={useBooks}
                  showEditorialCode={false}
                  description="Confira nossa seleção de obras que estão em alta. Descubra novos títulos para sua coleção!"
                  borderColor="#EC4899"
                  bg="#FDF2F8"
                />
              )}
              <HStack
                paddingX="5%"
                w="100%"
                boxSizing="border-box"
                overflowX="auto"
                css={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  'scrollbar-width': 'none',
                  '@media (min-width: 768px)': {
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    overflowX: 'unset',
                  },
                }}
              >
                {tags}
              </HStack>
              <VStack paddingX="5%" spacing={2} paddingBottom="80px" width="100%" boxSizing="border-box" margin="auto">
                <Input value={search} onValueChange={setSearch} label="Busca" rightElement={<AiOutlineSearch />} />
                <BookGrid
                  showEditorialCode={false}
                  filters={{
                    search,
                    categories: Array.from(tagSet).join(','),
                    ...(isMagazine ? { type: 'magazine' } : { type: isBook ? 'book' : 'audiobook', random: 'true' }),
                    limit: '50',
                    product_id: product?.id,
                  }}
                />
              </VStack>
            </VStack>
          </TabPanel>

          {/* Lendo */}
          <TabPanel>
            <HStack paddingX="5%" w="100%" boxSizing="border-box" overflowX="hidden">
              <BookGrid
                mutationHook={useMyReadingBooks as any}
                emptyTitle="Você ainda não está lendo nenhuma obra"
                emptyDescription="As obras que você começar a ler aparecerão aqui."
              />
            </HStack>
          </TabPanel>

          {acervoFirst && meusPanel}

          {/* Revistas Personalizadas */}
          {/*       {isMagazine && hasDigitalMagazines && (
            <TabPanel padding="0px">
              <VStack mt="20px" width="100%" spacing={5} align="flex-start">
                <InfoBox
                  title="Revistas Personalizadas"
                  description="Confira todas as revistas personalizadas disponíveis para você, organizadas por mês de referência."
                  borderColor="#8B5CF6"
                  bg="#F5F3FF"
                />
                <MagazineGrid agreementId={id ?? undefined} />
              </VStack>
            </TabPanel>
          )} */}

          {/* Favoritos */}
          <TabPanel>
            <HStack paddingX="5%" w="100%" boxSizing="border-box" overflowX="hidden">
              <BookGrid
                mutationHook={useGetFavoritedBooksMutation}
                emptyTitle="Você ainda não favoritou nenhuma obra"
                emptyDescription="Favorite obras para encontrá-las facilmente aqui."
              />
            </HStack>
          </TabPanel>
        </TabPanels>
      </TopBookstoreMenu>
    </MainTemplate>
  );
}
